import { useCallback, useMemo, useState } from 'react';

import { CommonSelectProps } from '../types/CommonSelectProps';
import { ALL_OPTION } from '../types/allOption';

type SelectProps = Pick<CommonSelectProps, 'hasError'> & {
  value: string[];
  setValue: (selectValue: string[]) => void;
};

type UseSelectMultiReturn = {
  /** Returns false if there are errors */
  validate: () => boolean;
  /** Props to spread in SearchSelect or StandardSelect */
  props: SelectProps;
};

export const useSelectMulti = (
  initialValue?: string[],
  useAllOption?: boolean,
  onChange?: (value: string[]) => void
): UseSelectMultiReturn => {
  const [selectValue, setSelectValue] = useState<string[]>(init(initialValue));
  const [hasError, setHasError] = useState(false);

  const props: SelectProps = useMemo(() => {
    const setValue = (value: string[]) => {
      const newValue = useAllOption ? getValueUsingAllOption(value, selectValue) : value;
      setSelectValue(newValue);
      onChange?.(newValue);
      setHasError(false);
    };

    return { hasError, value: selectValue, setValue } satisfies SelectProps;
  }, [useAllOption, hasError, onChange, selectValue]);

  const validate = useCallback(() => {
    if (validateHasSelected(selectValue)) {
      setHasError(false);
      return true;
    } else {
      setHasError(true);
      return false;
    }
  }, [selectValue]);

  return {
    validate,
    props,
  };
};

const validateHasSelected = (value: string[]): boolean => {
  return !!value.length && !!value[0];
};

const init = (value?: string[]): string[] => {
  return value ?? [];
};

const getValueUsingAllOption = (newValue: string[], previousValue: string[]) => {
  if (newValue.length === 0) {
    // Default value if none is selected is ALL_OPTION
    return [ALL_OPTION];
  }

  if (newValue.includes(ALL_OPTION)) {
    if (newValue.length === 1) {
      return newValue;
    }
    if (previousValue.includes(ALL_OPTION)) {
      return newValue.filter(id => id !== ALL_OPTION);
    }
    return [ALL_OPTION];
  }
  return newValue;
};
